import { Spinner } from '@frontend/components/common';
import { ICONS, Plus } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { UserSelectV2 } from '@frontend/user/select';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { FormattedMessage } from 'react-intl';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useSelectReceivers from './select-receivers.controller';

const ID = 'select-receivers';

const SelectReceivers = (props: { wizard_id: string }) => {
    const viewProps = useSelectReceivers(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='flex items-start justify-start self-stretch'>
                <div className='text-foreground-default justify-start text-base font-medium'>
                    <FormattedMessage
                        id='platform.wizard.select_receivers.instructions'
                        description='Instructions shown when selecting receivers in the platform PUDO wizard'
                        defaultMessage='Please select the receiver(s)'
                    />
                </div>
            </div>
            {viewProps.delayedReceiversSearch ? (
                <p className='text-brand-muted text-sm leading-normal font-medium'>
                    <FormattedMessage
                        id='platform.wizard.select-receivers.delayed-search-hint'
                        defaultMessage='Type at least 3 characters to search'
                        description='Hint shown on platform select-receivers when delayed_receivers_search is enabled'
                    />
                </p>
            ) : null}

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.selectedReceivers.some((receiver) => receiver !== null) ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.USER className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}</div>
                        </div>
                    </div>
                </div>

                {viewProps.isLoading ? (
                    <Spinner />
                ) : (
                    <div className='flex w-full flex-col gap-3'>
                        {viewProps.selectedReceivers.map((receiverId, index) => (
                            <div
                                key={index}
                                className='flex w-full items-center gap-3'>
                                <div className='flex-1'>
                                    <UserSelectV2
                                        id={index.toString()}
                                        hideLabel
                                        value={receiverId}
                                        onChange={(user) => viewProps.updateReceiver(index, user)}
                                        isClearable
                                        {...(viewProps.delayedReceiversSearch ? { minSearchCharacters: 3 } : {})}
                                        showSelectedDetails
                                        fetchOverwrite={viewProps.fetchReceiverOptions}
                                        subset={viewProps.receiverSubsets[index]}
                                    />
                                </div>
                                {viewProps.selectedReceivers.length > 1 && (
                                    <button
                                        type='button'
                                        onClick={() => viewProps.removeReceiver(index)}
                                        className='text-foreground-default hover:text-foreground-emphasis shrink-0 transition-colors'
                                        aria-label='Remove receiver'>
                                        <ICONS.BUTTON.DELETE className='size-5' />
                                    </button>
                                )}
                            </div>
                        ))}

                        <button
                            type='button'
                            onClick={viewProps.addReceiver}
                            className='flex w-full items-center justify-end gap-3'>
                            <Plus className='size-3.5' />
                            <p className='text-foreground-default text-base leading-normal font-medium'>
                                <FormattedMessage
                                    id='platform.wizard.select_receivers.add_another'
                                    description='Button to add another receiver in the platform PUDO wizard'
                                    defaultMessage='Add another receiver'
                                />
                            </p>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectReceivers;
